import React from 'react';
import {
    BlankModal,
    HStack,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
    FormRow,
} from '@cad-arc';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { useForm, useWatch } from 'react-hook-form';
import { FormSelect } from '../forms/integrated-form-controls/form-select';
import { IntegratedFormProvider } from '../forms/integrated-form-provider';
import { FormCancelButton } from '../forms/integrated-form-components/form-cancel-button';
import { FormSaveButton } from '../forms/integrated-form-components/form-save-button';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useApiClient } from '@m43/core';
import { WorkspacesResource, WorkspaceModeEnumType } from '@mark43/cad-api';
import { FormBody } from '../forms/integrated-form-components/form-body';

type AppModeOption = { value: string; label: string; mode: WorkspaceModeEnumType };
type WorkspaceUserOption = { value: number; label: string; mode: WorkspaceModeEnumType };
type ResetWorkspaceForm = { user: WorkspaceUserOption; appMode: AppModeOption };

export type ResetUserWorkspaceModalType = typeof ResetUserWorkspaceModal;

const appModeOptions = [
    { label: 'Desktop', value: 'ELECTRON' },
    { label: 'Web', value: 'WEB' },
];

export function ResetUserWorkspaceModal(): React.ReactElement {
    const apiClient = useApiClient();
    const modalControls = useFullscreenModalControls();
    const resetWorkspaceForm = useForm<ResetWorkspaceForm>({
        defaultValues: { appMode: appModeOptions[0] },
    });

    const { data: userProfileViews, isLoading } = useQuery(['user-profile-views'], async () => {
        return apiClient
            .request<WorkspacesResource.GetWorkspaceResetBootstrapView>({
                method: 'GET',
                url: `workspaces/reset/bootstrap`,
            })
            .then(
                (response) =>
                    response.data.data?.users.map((u) => ({
                        label: u.primaryEmail,
                        value: u.userId,
                        mode: u.mode,
                    })) ?? []
            );
    });

    const selectedMode = useWatch({ name: 'appMode', control: resetWorkspaceForm.control });
    const userProfileOptions = (userProfileViews ?? []).filter(
        (view) => view.mode === selectedMode?.value
    );

    const onClose = React.useCallback(() => {
        modalControls.close();
    }, [modalControls]);

    const resetWorkspaceMutation = useMutation(
        (form: ResetWorkspaceForm) => {
            return apiClient.request<WorkspacesResource.ResetWorkspaceStateForUser>({
                method: 'POST',
                url: `workspaces/reset/${form.user.value}/`,
                headers: {
                    // Most workspace logic is based on this header,
                    // so we modify it based on the selected option.
                    'x-cad-app-mode': form.appMode.value,
                },
            });
        },
        {
            onSuccess: () => onClose(),
        }
    );

    return (
        <IntegratedFormProvider form={resetWorkspaceForm} mutation={resetWorkspaceMutation}>
            <BlankModal size="lg" onClose={onClose} isOpen={true}>
                <ModalHeader>Reset User Workspace</ModalHeader>
                <ModalBody>
                    {isLoading ? (
                        <HStack justifyContent="center">
                            <Spinner />
                        </HStack>
                    ) : (
                        <FormBody>
                            <FormRow>
                                <FormSelect
                                    label="User"
                                    name="user"
                                    control={resetWorkspaceForm.control}
                                    options={userProfileOptions}
                                    rules={{ required: 'Required' }}
                                    length="md"
                                />
                                <FormSelect
                                    name="appMode"
                                    control={resetWorkspaceForm.control}
                                    label="App Mode"
                                    options={appModeOptions}
                                    rules={{ required: 'Required' }}
                                    length="md"
                                />
                            </FormRow>
                        </FormBody>
                    )}
                </ModalBody>
                <ModalFooter>
                    <FormCancelButton onClick={onClose} />
                    <FormSaveButton>Reset Workspace</FormSaveButton>
                </ModalFooter>
            </BlankModal>
        </IntegratedFormProvider>
    );
}
